import type { Edge, Node } from '@xyflow/react';
import type { SubjectEdge } from '../domain/entities/content.entity';
import { formatPercentage } from './display';
import {
  buildSubjectGraph,
  type GraphSubject,
  type SubjectGraph,
  type SubjectNodeData,
  type SubjectRole,
} from './subject-graph-layout';

/**
 * Paleta categórica de 8 cores, uma por raiz, com um passo para cada tema.
 *
 * Não sai do `tokens.ts` do design-system: ele só tem 5 matizes, e verde e
 * vermelho ali são sucesso e erro. Esta é a paleta validada de 8 slots (CVD e
 * visão normal, nas superfícies clara e escura). Mora aqui por ser de uso único;
 * sobe para o design-system quando outro app precisar dela.
 *
 * Com mais de 8 raízes a cor repete em ciclo (decisão do plano). A cor nunca
 * está sozinha: o nó de perto mostra o nome da raiz, e a legenda lista as raízes.
 */
const PALETTE = {
  light: [
    '#2a78d6',
    '#eb6834',
    '#1baf7a',
    '#eda100',
    '#e87ba4',
    '#008300',
    '#6250d6',
    '#e34948',
  ],
  dark: [
    '#3987e5',
    '#d95926',
    '#199e70',
    '#c98500',
    '#d55181',
    '#008300',
    '#9085e9',
    '#e66767',
  ],
} as const;

/** Cor neutra do assunto solto, que não pertence a raiz nenhuma. */
const ISOLATED_COLOR = { light: '#a3a3a3', dark: '#525252' } as const;

export function subjectColor(
  rootIndex: number | null,
  isDark: boolean,
): string {
  const theme = isDark ? 'dark' : 'light';
  if (rootIndex === null) return ISOLATED_COLOR[theme];
  return PALETTE[theme][rootIndex % PALETTE[theme].length];
}

/** `1 filho`, `2 filhos`. */
export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

const ROLE_LABEL: Record<SubjectRole, string> = {
  root: 'raiz',
  branch: 'ramo',
  leaf: 'folha',
  isolated: 'solto',
};

/**
 * A linha de baixo do nó de perto. A incidência vem primeiro porque a caixa tem
 * largura fixa e a linha corta no fim: com muitos pais, o que some é a
 * contagem, não o número que o admin veio ver. Vem rotulada ("incid."), porque
 * o número solto não diz o que mede e o `title` não chega a quem usa teclado
 * ou leitor de tela; sem cálculo é "—", como na tabela da aba Assuntos.
 */
export function nodeMeta(data: SubjectNodeData): string {
  return [
    `incid. ${formatPercentage(data.generalIncidence)}`,
    ROLE_LABEL[data.role],
    data.role === 'branch' || data.role === 'leaf' ? data.rootName : null,
    data.childCount > 0 ? plural(data.childCount, 'filho', 'filhos') : null,
    data.parentCount > 1 ? `${data.parentCount} pais` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** O texto do hover: o nome inteiro, que o nó corta, e a incidência por extenso. */
export function nodeTitle(data: SubjectNodeData): string {
  const incidence =
    data.generalIncidence === null
      ? 'sem cálculo'
      : formatPercentage(data.generalIncidence);
  return `${data.label} · incidência geral: ${incidence}`;
}

export type ZoomBand = 'far' | 'mid' | 'near';

/** Cortes de zoom do nível de detalhe do nó. */
export const MID_ZOOM = 0.45;
export const NEAR_ZOOM = 0.9;

/**
 * Faixa de zoom, não o zoom cru: o nó só re-renderiza quando a faixa muda, e
 * não a cada tick da roda.
 */
export function zoomBand(zoom: number): ZoomBand {
  if (zoom >= NEAR_ZOOM) return 'near';
  if (zoom >= MID_ZOOM) return 'mid';
  return 'far';
}

/** Pais → filhos (`down`) e filhos → pais (`up`), por todas as arestas. */
function adjacency(edges: Edge[]) {
  const up = new Map<string, string[]>();
  const down = new Map<string, string[]>();
  const link = (m: Map<string, string[]>, from: string, to: string) => {
    const list = m.get(from);
    if (list) list.push(to);
    else m.set(from, [to]);
  };
  for (const e of edges) {
    link(up, e.target, e.source);
    link(down, e.source, e.target);
  }
  return { up, down };
}

/** Tudo que se alcança a partir dos `ids` seguindo `links`, sem eles. */
function reach(ids: string[], links: Map<string, string[]>): Set<string> {
  const seen = new Set<string>();
  const stack = [...ids];
  while (stack.length > 0) {
    for (const next of links.get(stack.pop()!) ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      stack.push(next);
    }
  }
  return seen;
}

export interface SearchFocus {
  /** Todos os assuntos com esse nome (homônimos inclusos). */
  matchIds: string[];
  /** O primeiro deles na ordem do layout, onde a câmera centraliza. */
  centerId: string;
  /** O que fica destacado; o resto fica atenuado. */
  highlightIds: Set<string>;
  /** Do entorno dos achados, quantos estão acima e quantos abaixo deles. */
  ancestorCount: number;
  descendantCount: number;
}

/**
 * O que a busca centraliza e destaca. Nome compara sem caixa e sem espaço nas
 * pontas. Com uma visão (`?view=`) ativa, a busca só marca os assuntos achados e
 * deixa o atenuado da visão como está; sem visão, destaca o entorno inteiro.
 */
export function searchFocus(
  query: string,
  nodes: Node<SubjectNodeData>[],
  edges: Edge[],
  viewActive: boolean,
): SearchFocus | null {
  const wanted = query.trim().toLocaleLowerCase();
  if (!wanted) return null;
  const matchIds = nodes
    .filter((n) => n.data.label.trim().toLocaleLowerCase() === wanted)
    .map((n) => n.id);
  if (matchIds.length === 0) return null;
  const { up, down } = adjacency(edges);
  const matches = new Set(matchIds);
  // Homônimo que é ancestral de outro conta como achado, não como entorno.
  const ancestors = [...reach(matchIds, up)].filter((id) => !matches.has(id));
  const descendants = [...reach(matchIds, down)].filter(
    (id) => !matches.has(id),
  );
  return {
    matchIds,
    centerId: matchIds[0],
    highlightIds: viewActive
      ? matches
      : new Set([...matchIds, ...ancestors, ...descendants]),
    ancestorCount: ancestors.length,
    descendantCount: descendants.length,
  };
}

/**
 * O mapa só com as raízes ligadas. Cada raiz leva tudo o que está abaixo dela,
 * por qualquer aresta: um assunto com pais em duas raízes aparece se qualquer
 * uma estiver ligada, junto com o caminho até ela. O layout se refaz com o que
 * ficou, mas a cor de cada raiz e as contagens de pais e filhos vêm do acervo
 * inteiro — desligar uma raiz não repinta as outras nem muda o "N pais".
 */
export function filterByRoots(
  full: SubjectGraph,
  subjects: GraphSubject[],
  edges: SubjectEdge[],
  hiddenRootIds: Set<string>,
  showIsolated: boolean,
): SubjectGraph {
  if (hiddenRootIds.size === 0 && showIsolated) return full;
  const active = full.roots
    .map((r) => r.id)
    .filter((id) => !hiddenRootIds.has(id));
  const visible = new Set([
    ...active,
    ...reach(active, adjacency(full.edges).down),
  ]);
  if (showIsolated) {
    for (const n of full.nodes)
      if (n.data.role === 'isolated') visible.add(n.id);
  }
  const part = buildSubjectGraph(
    subjects.filter((s) => visible.has(s.id)),
    edges,
  );
  const fullData = new Map(full.nodes.map((n) => [n.id, n.data]));
  const rootIndex = new Map(full.roots.map((r, i) => [r.id, i]));
  return {
    ...part,
    nodes: part.nodes.map((n) => ({
      ...n,
      data: {
        ...n.data,
        rootIndex:
          n.data.rootIndex === null ? null : rootIndex.get(n.data.rootId)!,
        parentCount: fullData.get(n.id)!.parentCount,
        childCount: fullData.get(n.id)!.childCount,
      },
    })),
  };
}

/**
 * Zoom mínimo que ainda cabe o mapa inteiro numa tela pequena. O `fitView` não
 * desce abaixo do `minZoom`; fixo em 0.02, um acervo largo abria cortado nas
 * pontas. A tela de referência (600×300) é menor que o canvas, de propósito.
 */
export function minZoomToFit(nodes: Node[]): number {
  let width = 1;
  let height = 1;
  for (const n of nodes) {
    width = Math.max(width, n.position.x + (n.width ?? 0));
    height = Math.max(height, n.position.y + (n.height ?? 0));
  }
  return Math.min(0.02, 600 / width, 300 / height);
}

/**
 * Cor, destaque e atenuação de cada nó, juntando o foco da visão (`?view=`,
 * `viewMemberIds`) com o da busca. Sem busca, vale a visão. Com busca e sem
 * visão, o entorno do achado fica destacado e o resto atenuado. Com os dois, a
 * busca só marca o que achou (e o tira do atenuado, para ser visto) e o resto
 * segue como a visão deixou.
 */
export function emphasizeNodes(
  nodes: Node<SubjectNodeData>[],
  viewMemberIds: Set<string> | null,
  focus: SearchFocus | null,
  isDark: boolean,
): Node<SubjectNodeData>[] {
  return nodes.map((n) => {
    let highlighted = false;
    let dimmed = false;
    if (viewMemberIds) {
      highlighted = viewMemberIds.has(n.id);
      dimmed = !highlighted;
    }
    if (focus) {
      if (focus.highlightIds.has(n.id)) {
        highlighted = true;
        dimmed = false;
      } else if (!viewMemberIds) {
        highlighted = false;
        dimmed = true;
      }
    }
    return {
      ...n,
      data: {
        ...n.data,
        color: subjectColor(n.data.rootIndex, isDark),
        highlighted,
        dimmed,
      },
    };
  });
}
